import { Alert } from "@xola/ui-kit";
import React from "react";
import { Permission, Seller } from "types";
import { useSeller } from "../../../hooks/useAccount";
import { useModal } from "../../../hooks/useVisibility";
import { Currency } from "../../Common/Currency";
import { IsGranted } from "../../Common/IsGranted";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { PayoutTopUpModal } from "./PayoutTopUpModal";
import { RefundReserveModal } from "./RefundReserveModal";

export const PayoutSummary = () => {
    const seller = useSeller();

    const topUpModal = useModal(false);
    const refundModal = useModal(false);

    const balance = seller.balance ?? 0;
    const reserveActualAmount = seller.preferences?.payout?.escrow?.actualAmount ?? 0;
    const availableToPayout = Math.max(balance - reserveActualAmount, 0);

    return (
        <div className="px-6 py-3 print:max-h-20">
            <Alert color="primary">
                <div className="my-2 flex justify-between rounded px-4">
                    <div>
                        <div className="flex items-center">
                            <span className="mr-2 text-base font-bold">Refund Reserve:</span>
                            <span className="mr-2 text-xl font-bold" data-testid="payout-summary-refund-reserve">
                                <Currency>{getRefundReserve(seller)}</Currency>
                            </span>
                            <IsGranted permissions={Permission.EDIT_REFUND_RESERVE}>
                                <SecondaryButton size="small" className="print:hidden" onClick={refundModal.show}>
                                    Adjust Refund Reserves
                                </SecondaryButton>
                            </IsGranted>
                        </div>
                        <div className="mt-2 text-base">Fund Reserve maintains a positive balance used for refunds</div>
                    </div>
                    <div>
                        <div className="flex items-center justify-end">
                            <span className="mr-2 text-base font-bold">Available for Payout: </span>
                            <span className="mr-2 text-xl font-bold" data-testid="payout-summary-available-for-payout">
                                <Currency>{availableToPayout}</Currency>
                            </span>
                            <IsGranted permissions={Permission.TOP_UP_VIEW}>
                                <SecondaryButton size="small" className="print:hidden" onClick={topUpModal.show}>
                                    Top up
                                </SecondaryButton>
                            </IsGranted>
                        </div>
                        <div className="mt-2 text-base">
                            Your balance less Refund Reserve is what is available for payout.
                        </div>
                    </div>
                </div>
            </Alert>
            <PayoutTopUpModal isOpen={topUpModal.isVisible} onClose={topUpModal.hide} />
            <RefundReserveModal isOpen={refundModal.isVisible} onClose={refundModal.hide} />
        </div>
    );
};

const getRefundReserve = (seller: Seller) => {
    const { escrow } = seller.preferences.payout;
    const reserveActualAmount = escrow?.actualAmount;
    const { balance } = seller;
    if (!balance || !reserveActualAmount) {
        return 0;
    }

    return balance < reserveActualAmount ? balance : reserveActualAmount;
};
